import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, RefreshCw, Wrench, ChevronDown } from 'lucide-react';
import { adminAvisosAPI, adminStoresAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

/*
  Lo que necesita atención, y el botón para arreglarlo al lado.

  Nace de una lección: cuatro promociones llevaron entre uno y dos meses
  terminadas y a la vista sin que nadie se enterara. La única señal era que
  dejaban de aparecer en la landing — un silencio, no un aviso. Esa vigilancia
  concreta se retiró el 9-sep-2026 junto con la vigencia misma —sin fechas que
  se cumplan solas no hay nada que se caiga sin que alguien lo decida— pero la
  regla que dejó es la que gobierna esta pantalla.

  Dos decisiones que dan forma a esta pantalla:

  1. **Está escrita para el administrador, no para nosotros.** Nada de
     «tiendas con `access_pin_hash` nulo»: «tiendas que no pueden cobrar».
     Dice qué pasa, qué se rompe si no se atiende, y qué hacer.

  2. **Se arregla aquí.** Un aviso que obliga a ir a buscar la promoción a otra
     sección es un aviso que nadie atiende. Cada línea trae su acción.

  Lo que necesita a un programador no sale arriba: no hay nada que el
  administrador pueda tocar y solo añadiría ruido. Va al registro técnico del
  final, y al log del servidor para que quede constancia aunque nadie abra esto.
*/

const NIVELES = {
  alto: { caja: 'bg-red-50 border-red-200', titulo: 'text-red-900', icono: 'text-red-500', Icono: AlertCircle },
  medio: { caja: 'bg-amber-50 border-amber-200', titulo: 'text-amber-900', icono: 'text-amber-500', Icono: AlertTriangle },
};

const botonBase =
  'px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const Item = ({ item, acciones, alTerminar }) => {
  const [ocupado, setOcupado] = useState(null);
  const [pin, setPin] = useState(null);

  const correr = async (nombre, fn, mensaje) => {
    setOcupado(nombre);
    try {
      const r = await fn();
      toast({ title: 'Listo', description: mensaje });
      if (nombre === 'generar_pin') setPin(r?.data?.pin ?? null);
      else await alTerminar();
    } catch (e) {
      toast({ title: 'No se pudo', description: e.message || 'Intenta de nuevo.', variant: 'destructive' });
    } finally {
      setOcupado(null);
    }
  };

  return (
    <div className="py-3 border-t border-black/5 first:border-t-0">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-sm text-gray-900 font-medium">{item.texto}</span>
        {item.nota && <span className="text-xs text-gray-500">{item.nota}</span>}
      </div>

      {pin ? (
        <div className="mt-2 rounded-lg bg-white border border-gray-300 px-3 py-2">
          <div className="text-xs text-gray-500">PIN de la caja</div>
          <div className="text-2xl font-bold tracking-widest text-gray-900">{pin}</div>
          <div className="text-xs text-gray-500 mt-0.5">Anótalo y pásaselo a la tienda: no se vuelve a mostrar.</div>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {acciones.includes('generar_pin') && (
            <button
              className={`${botonBase} bg-gray-900 border-gray-900 text-white hover:bg-gray-800`}
              disabled={!!ocupado}
              onClick={() => correr('generar_pin',
                () => adminStoresAPI.rotateAccessPin(item.id),
                'PIN generado.')}
            >
              {ocupado === 'generar_pin' ? 'Generando…' : 'Generar PIN'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const Aviso = ({ aviso, alTerminar }) => {
  const estilo = NIVELES[aviso.nivel] ?? NIVELES.medio;
  const { Icono } = estilo;

  return (
    <div className={`rounded-2xl border ${estilo.caja} p-5`}>
      <div className="flex items-start gap-3">
        <Icono className={`w-5 h-5 flex-shrink-0 mt-0.5 ${estilo.icono}`} />
        <div className="min-w-0">
          <h3 className={`font-semibold text-base ${estilo.titulo}`}>{aviso.titulo}</h3>
          <p className="text-sm text-gray-700 mt-1 leading-relaxed">{aviso.detalle}</p>
          {aviso.que_hacer && (
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">
              <span className="font-semibold text-gray-800">Qué hacer: </span>{aviso.que_hacer}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 rounded-xl bg-white/70 border border-black/5 px-4">
        {aviso.items.map((item) => (
          <Item key={`${item.tipo}-${item.id}`} item={item} acciones={aviso.acciones ?? []} alTerminar={alTerminar} />
        ))}
      </div>
    </div>
  );
};

const RegistroTecnico = ({ tecnicos }) => {
  const [abierto, setAbierto] = useState(false);
  if (!tecnicos?.length) return null;

  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-50">
      <button onClick={() => setAbierto((v) => !v)} className="w-full flex items-center gap-3 p-4 text-left">
        <Wrench className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-gray-700">Registro técnico ({tecnicos.length})</div>
          <div className="text-xs text-gray-500">Esto no se arregla desde aquí; queda anotado para que lo revise el equipo.</div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${abierto ? 'rotate-180' : ''}`} />
      </button>
      {abierto && (
        <div className="px-4 pb-4 space-y-3">
          {tecnicos.map((t) => (
            <div key={t.clave} className="rounded-xl bg-white border border-gray-200 p-3">
              <div className="text-sm font-semibold text-gray-800">{t.titulo}</div>
              <p className="text-xs text-gray-600 mt-1">{t.detalle}</p>
              <ul className="mt-2 space-y-1">
                {t.items.map((i, n) => (
                  <li key={n} className="text-xs text-gray-600">
                    {i.texto} <span className="text-gray-400">— {i.nota}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Notificaciones = () => {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await adminAvisosAPI.getAll();
      setDatos(r?.data ?? null);
      setFallo(false);
    } catch {
      setFallo(true);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div className="p-6 space-y-6">
      <Toaster />
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Notificaciones</h2>
          <p className="text-gray-500">Lo que necesita tu atención, y cómo arreglarlo desde aquí.</p>
        </div>
        <button
          onClick={cargar}
          disabled={cargando}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${cargando ? 'animate-spin' : ''}`} />
          Volver a revisar
        </button>
      </div>

      {fallo ? (
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 text-sm text-gray-600">
          No se pudieron cargar las notificaciones. Vuelve a intentarlo en un momento.
        </div>
      ) : cargando && !datos ? (
        <div className="text-center py-12 text-gray-500">Revisando...</div>
      ) : (
        <>
          {datos?.avisos?.length > 0 ? (
            <div className="space-y-4">
              {datos.avisos.map((a) => <Aviso key={a.clave} aviso={a} alTerminar={cargar} />)}
            </div>
          ) : (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-emerald-900">Todo en orden</div>
                <p className="text-sm text-emerald-800 mt-0.5">
                  Todas las tiendas pueden cobrar y los ahorros de los clientes cuadran.
                </p>
              </div>
            </div>
          )}

          <RegistroTecnico tecnicos={datos?.tecnicos} />
        </>
      )}
    </div>
  );
};

export default Notificaciones;
